import {Platform} from "@variocube/app-api";
import {findBoxByNumber, getBoxTypes} from "@variocube/app-common";
import {
    BoxAccess,
    BoxStorage,
    BoxTypeSelection, BoxTypeSelectionResult,
    classNames,
    layout,
    MessageView,
    PendingView,
    PushButton,
    typography,
    useAsync,
    useAsyncCallback,
    useBoxes,
    usePlatform,
    usePreventDeactivate,
    View,
    ViewActions,
    ViewContent,
    ViewHeader,
    ViewTitle
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useState} from "react";
import {Order, OrderOccupancyType, Shop} from "../types";
import {logger} from "../logger";
import {useLocalization} from "../../i18n";
import { auditLog } from "../auditLog";

const log = logger.sub("DeliveryFlow");

interface DeliveryFlowProps {
    order: Order;
    shop: Shop;
    onDone: () => void;
}

export function DeliveryFlow({order, shop, onDone}: DeliveryFlowProps) {

    usePreventDeactivate();

    const {t} = useLocalization();
    const boxes = useBoxes();
    const boxTypes = getBoxTypes(boxes).filter((boxType) => boxType != "Admission");

    const [boxSelection, setBoxSelection] = useState<BoxTypeSelectionResult>();

    const handleOtherType = useCallback(() => setBoxSelection(undefined), []);

    if (!boxSelection) {
        return (
            <View className={layout.vertical}>
                <ViewHeader className={classNames(layout.paddingMedium, typography.alignCenter)}>
                    <ViewTitle>
                        {t("SelectTypeTitle")}
                    </ViewTitle>
                    <ViewActions>
                        <PushButton onClick={onDone}>
                            {t("Cancel")}
                        </PushButton>
                    </ViewActions>
                </ViewHeader>
                <ViewContent>
                    <BoxTypeSelection
                        onSelect={setBoxSelection}
                        types={boxTypes}
                        strings={t}
                        className={layout.fill}
                    />
                </ViewContent>
            </View>
        );
    }
    else {
        return (
            <Storage order={order} shop={shop} boxSelection={boxSelection} onDone={onDone} onOtherType={handleOtherType}/>
        );
    }
}

interface StorageProps {
    order: Order;
    shop: Shop;
    boxSelection: BoxTypeSelectionResult;
    onOtherType: () => void;
    onDone: () => void;
}

function Storage({order, shop, boxSelection, onOtherType, onDone}: StorageProps) {
    const platform = usePlatform();
    const occupyState = useAsync(() => occupy(platform, boxSelection, order), [platform, boxSelection, order]);

    const {t} = useLocalization();

    const handleConfirm = useAsyncCallback(async () => {
        if (!occupyState.result) {
            throw new Error("invalid state");
        }
        log.info("Confirming storage", occupyState.result.occupancy);
        await platform.confirmOccupancy(occupyState.result.occupancy.uuid);
        onDone();
    }, [platform, occupyState.result]);

    const handleWrongSize = useCallback(async () => {
        log.info("User selected wrong size.")

        if (!occupyState.result) {
            throw new Error("invalid state");
        }
		onOtherType();
    }, [occupyState.result, platform]);

    const handleMaintenance = useCallback(async () => {
        log.info("User requested maintenance.");
        if (!occupyState.result) {
            throw new Error("invalid state");
        }
        const occupancy = occupyState.result.occupancy;
         if (occupancy) {
            try {
                await platform.requireBoxMaintenance(occupancy.boxNumber); 
                await auditLog(platform, "Maintenance", {boxNumber: occupancy.boxNumber});
				onOtherType();
            } catch (error) {
                log.error("Error while handling maintenance", error);
            }
        }
    }, [occupyState.result, platform]);

    const handleCancel = useCallback(async () => {
        if (occupyState.result) {
            log.info("Canceling storage", occupyState.result.occupancy);
            await platform.cancelOccupancy(occupyState.result.occupancy.uuid);
        }
        onDone();
    }, [platform, occupyState.result, onDone]);

    if (handleConfirm.loading) {
        return <PendingView message={t("OccupancyConfirming")}/>;
    }
    else if (handleConfirm.error) {
        return (
            <MessageView message={`Confirming occupancy failed: ${handleConfirm.error}`}>
                <PushButton onClick={onDone}>
                    {t("Cancel")}
                </PushButton>
            </MessageView>
        );
    }
    else if (occupyState.loading) {
        return <PendingView message={t("OccupancyPending")}/>;
    }
    else if (occupyState.error) {
        return (
            <MessageView message={t("BoxTypeNotAvailable")}>
                <PushButton onClick={onOtherType}>
                    {t("SelectOtherSize")}
                </PushButton>
                <PushButton onClick={onDone}>
                    {t("Cancel")}
                </PushButton>
            </MessageView>
        );
    }
    else if (occupyState.result) {
        const {occupancy, box} = occupyState.result;

        if (occupancy && box) {
            return (
                <View>
                    <BoxStorage
                        platform={platform}
                        boxes={box}
                        onConfirm={handleConfirm.execute}
                        strings={t}
                        onCancel={handleCancel}
                        boxOpenTitle={t("ShopDeliveryStoreTitle", {box, order})}
                        boxOpenMessage={t("ShopDeliveryStoreMessage", {box, order})}
                        confirmTitle={t("ShopDeliveryConfirmTitle", {box, order})}
                        confirmMessage={t("ShopDeliveryConfirmMessage", {box, order})}
                        onWrongSize={handleWrongSize}
                        onMaintenance={handleMaintenance}
                    />
                </View>
            );
        }
    }
    return null;
}

async function occupy(platform: Platform, boxSelection: BoxTypeSelectionResult, order: Order) {
    log.info("Occupying box", {boxSelection, order});
    const occupancy = await platform.occupy({
        ...boxSelection,
        accessCodeShape: {
            alphabet: "123456789",
            length: 7
        },
        content: {
            order,
            type: OrderOccupancyType.Outbound,
        },
    });

    const box = await findBoxByNumber(platform, occupancy.boxNumber);

    return {occupancy, box};
}
